import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/mangaData';

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSuccess(true);
      setSubmitting(false);
      return;
    }

    try {
      formData.delete('website_alt');
      const response = await fetch('https://readdy.ai/api/form/d9p0on0senbniolesnh0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '36漫画网',
    url: 'https://36manhua.com',
    description: '专业漫画在线阅读平台，提供咒术回战等热门漫画全集无删减免费阅读',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@36manhua.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '36漫画网的漫画是否全部免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，36漫画网上所有漫画均免费在线阅读，无需付费或开通会员。',
        },
      },
      {
        '@type': 'Question',
        name: '咒术回战漫画是否完整无删减？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '36漫画网提供的咒术回战漫画为全集无删减版，所有章节一网打尽，享受原汁原味的完整故事。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们会在日本连载更新后第一时间同步，通常每周更新一次，确保读者不会错过任何精彩内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何推荐想看的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过联系我们页面提交漫画推荐，我们会根据读者需求尽快收录热门漫画。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="mt-6 mb-16 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
              关于36漫画网
            </h1>
            <p className="mt-4 text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              36漫画网成立于2024年，是一个专注于漫画在线免费阅读的平台。我们致力于为漫画爱好者提供最优质的阅读体验，
              让每一位读者都能畅享原汁原味的漫画故事。
            </p>
          </section>

          {/* Brand Introduction */}
          <section id="brand" className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="rounded-xl overflow-hidden bg-background-100 aspect-video">
                  <img
                    src="https://readdy.ai/api/search-image?query=Japanese%20manga%20library%20aesthetic%20interior%2C%20warm%20wooden%20bookshelves%20filled%20with%20comic%20books%2C%20soft%20ambient%20lighting%2C%20cozy%20reading%20atmosphere%2C%20clean%20minimalist%20design&width=800&height=450&seq=about-brand-manga&orientation=landscape"
                    alt="36漫画网品牌形象"
                    className="w-full h-full object-cover"
                    title="36漫画网 - 专业漫画阅读平台"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                  我们的使命
                </h3>
                <p className="text-sm text-foreground-600 leading-loose mb-4">
                  36漫画网诞生的初衷很简单——让每个人都能轻松接触并享受漫画的魅力。我们深知，漫画不仅是娱乐，更是一种艺术表达和文化传播的媒介。
                </p>
                <p className="text-sm text-foreground-600 leading-loose mb-4">
                  从咒术回战到海贼王，从鬼灭之刃到进击的巨人，我们精心收录了众多热门漫画作品，确保每一部作品都完整无删减，让读者能够沉浸在原汁原味的故事中。
                </p>
                <p className="text-sm text-foreground-600 leading-loose">
                  作为一个完全免费的漫画平台，36漫画网始终坚持用户至上的原则，不断优化阅读体验，提升更新速度，努力成为最受漫画爱好者信赖的在线漫画阅读网站。
                </p>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              核心优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: 'ri-book-open-line', title: '全集无删减', desc: '所有漫画章节完整收录，无删减、无遗漏，还原最真实的故事内容。' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '同步日本连载进度，第一时间更新最新章节，追更绝不落后。' },
                { icon: 'ri-image-line', title: '高清画质', desc: '提供高清晰度漫画图片，细节分毫毕现，带来沉浸式阅读体验。' },
                { icon: 'ri-shield-check-line', title: '完全免费', desc: '全部内容免费阅读，无需付费、无需会员，让每个人都能畅享漫画乐趣。' },
              ].map((feat, i) => (
                <div key={i} className="p-6 rounded-xl bg-background-100">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${feat.icon} text-lg text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section id="reputation" className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm font-label">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }, (_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${
                              i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'
                            }`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <p className="text-xs text-foreground-400 mt-3">{t.date}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                  与我们取得联系
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed mb-6">
                  如有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或直接填写右侧表单，我们会尽快回复。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">邮箱</p>
                      <p className="text-sm text-foreground-900">contact@36manhua.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                      <i className="ri-wechat-line text-lg text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微信公众号</p>
                      <p className="text-sm text-foreground-900">36漫画网</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                      <i className="ri-weibo-line text-lg text-secondary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微博</p>
                      <p className="text-sm text-foreground-900">@36漫画网官方</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100 rounded-xl p-6"
                >
                  {formSuccess && (
                    <div className="mb-6 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                      <i className="ri-checkbox-circle-line mr-2"></i>
                      提交成功！感谢您的留言，我们会尽快回复。
                    </div>
                  )}
                  {formError && (
                    <div className="mb-6 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      <i className="ri-error-warning-line mr-2"></i>
                      {formError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div className="mb-5">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-3 rounded-lg bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i> 提交中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i> 提交留言
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {[
                { q: '36漫画网的漫画是否全部免费？', a: '是的，36漫画网上所有漫画均免费在线阅读，无需付费或开通会员。' },
                { q: '咒术回战漫画是否完整无删减？', a: '36漫画网提供的咒术回战漫画为全集无删减版，所有章节一网打尽，享受原汁原味的完整故事。' },
                { q: '漫画更新频率如何？', a: '我们会在日本连载更新后第一时间同步，通常每周更新一次，确保读者不会错过任何精彩内容。' },
                { q: '如何推荐想看的漫画？', a: '您可以通过联系表单提交漫画推荐，我们会根据读者需求尽快收录热门漫画。' },
                { q: '支持移动端阅读吗？', a: '36漫画网完全适配手机和平板设备，您可以在任何设备上畅快阅读漫画。' },
              ].map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                    {faq.q}
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}